import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Image as ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { adminApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const AdminCategoriesList = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const data = await adminApi.categories.getAll(token);
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: 'Error',
        description: 'Failed to load categories',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      await adminApi.categories.delete(categoryId, token);
      toast({
        title: 'Success',
        description: 'Category deleted successfully',
      });
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete category',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#be1f1a]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categories</h1>
          <p className="text-gray-600 mt-1">{categories.length} categories</p>
        </div>
        <Button onClick={() => navigate('/admin/categories/new')} className="bg-[#be1f1a] hover:bg-[#a71b17]">
          <Plus className="h-4 w-4 mr-2" />
          Add Category
        </Button>
      </div>

      {/* Categories Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Image</TableHead>
              <TableHead>Name (English)</TableHead>
              <TableHead>Name (Spanish)</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead className="text-center">Order</TableHead>
              <TableHead className="text-center">Show on Home</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-gray-500">
                  No categories found
                </TableCell>
              </TableRow>
            ) : (
              categories.map((category) => (
                <TableRow key={category._id || category.id}>
                  <TableCell>
                    {category.image ? (
                      <img
                        src={resolveMediaUrl(category.image)}
                        alt={category.name?.en}
                        className="w-12 h-12 object-cover rounded"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                        <ImageIcon className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    {(() => {
                      // Handle versioned schema
                      if (category.versions && category.versions.length > 0) {
                        const usVersion = category.versions.find(v => v.countryCode === 'US') || category.versions[0];
                        return usVersion?.name || category.slug;
                      }
                      // Handle old schema
                      return category.name?.en || category.slug;
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      // Handle versioned schema
                      if (category.versions && category.versions.length > 0) {
                        const coVersion = category.versions.find(v => v.countryCode === 'CO') || category.versions[0];
                        return coVersion?.name || category.slug;
                      }
                      // Handle old schema
                      return category.name?.es || category.slug;
                    })()}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-gray-600">{category.slug}</TableCell>
                  <TableCell className="text-center">{category.order || 0}</TableCell>
                  <TableCell className="text-center">
                    {category.showOnHome ? (
                      <span className="text-green-600 font-semibold">✓</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        category.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {category.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/categories/edit/${category._id || category.id}`)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(category._id || category.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AdminCategoriesList;
